import { useCallback } from 'react';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';

const NANO = 1_000_000_000; // 1 TON in nano

function tonToNano(amount: number): string {
  // amount is in TON, convert to nano units as a string
  return Math.floor(amount * NANO).toString();
}

function payloadFromString(label: string): string {
  // base64-encode a tiny BOC-like comment cell.
  // For demo/testnet this is a plain text payload; production TX should
  // serialize a proper Cell via @ton/core.
  if (typeof window === 'undefined') return '';
  try {
    return window.btoa(label);
  } catch {
    return '';
  }
}

export function useTonWallet() {
  const [tonConnectUI] = useTonConnectUI();
  const address = useTonAddress();

  const isConnected = Boolean(address);

  const send = useCallback(
    async (to: string, amountTon: number, comment: string) => {
      if (!tonConnectUI) throw new Error('TON Connect not available');
      const tx = {
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: to,
            amount: tonToNano(amountTon),
            payload: payloadFromString(comment),
          },
        ],
      };
      return tonConnectUI.sendTransaction(tx);
    },
    [tonConnectUI]
  );

  const stake = useCallback(
    (cellId: number, cellAddress: string, amountTon: number) =>
      send(cellAddress, amountTon, `stake:${cellId}`),
    [send]
  );

  const fund = useCallback(
    (agentId: number, agentAddress: string, amountTon: number) =>
      send(agentAddress, amountTon, `fund:${agentId}`),
    [send]
  );

  const spawn = useCallback(
    (treasuryAddress: string, amountTon: number, genesisHash: string) =>
      send(treasuryAddress, amountTon, `spawn:${genesisHash}`),
    [send]
  );

  const connect = useCallback(() => {
    tonConnectUI?.openModal();
  }, [tonConnectUI]);

  const disconnect = useCallback(() => {
    tonConnectUI?.disconnect();
  }, [tonConnectUI]);

  return { address, isConnected, send, stake, fund, spawn, connect, disconnect };
}
